// The shapes the server sends. They mirror the Go types one for one, and are
// written by hand rather than generated: the API is small, and a hand-written type
// is where a field that should never have been exposed gets noticed.

import type { Wording } from './i18n/catalog'
import type { StaffLanguage } from './staffText'
import type { LanguageCode, LanguageSettings } from './i18n/languages'
import type { PivotSpec } from '../reports/pivot/types'
import type { QueueEstimate } from './estimate'

/** The most a visit note may hold, mirroring queue.NoteLimit in the Go.
 *
 *  Duplicated rather than fetched because it is a constant of the product, not a
 *  setting: an organisation does not get to decide that its operators may write
 *  paragraphs about customers. The server refuses anything longer whatever this
 *  says, so the worst a drift here can do is let somebody type past the limit and
 *  be told about it a moment later. */
export const NOTE_LIMIT = 255

/** One entry of a visit's note history (GET /tickets/{id}/notes). */
export interface NoteEntry {
  id: string
  ticketId: string
  text: string
  staffId?: string
  /** Who wrote it; absent for a note carried over from before the history. */
  author?: string
  /** Where it was written: a desk (counterName), reception, or unknown. */
  place: 'desk' | 'reception' | 'unknown'
  counterId?: string
  counterName?: string
  at: string
  editedAt?: string
  /** Whether the caller may still edit or delete it. */
  editable?: boolean
}

export type TicketState =
  | 'waiting'
  | 'called'
  | 'serving'
  | 'wrapup'
  | 'parked'
  | 'no_show'
  | 'closed'
  | 'void'

export type Priority = 'standard' | 'priority' | 'vip'

export type TicketSource = 'kiosk' | 'reception' | 'counter' | 'mobile' | 'appointment'

export interface Transaction {
  id: string
  serviceId: string
  counterId?: string
  staffId?: string
  startedAt?: string
  endedAt?: string
  outcome?: string
  marks?: string[]
  transferredFrom?: string
  transferReason?: string
  /** What the desk recorded delivering within this piece of work, in the order it
   *  was added. See DeliveredService. */
  delivered?: DeliveredEntry[]
}

/** One delivered service recorded on a transaction. */
export interface DeliveredEntry {
  id: string
  deliveredServiceId: string
  outcomeCode?: string
  addedAt: string
  staffId?: string
}

export interface Ticket {
  id: string
  tenantId: string
  branchId: string
  displayNumber: string
  prefix: string
  sequence: number
  businessDate: string
  state: TicketState
  priority: Priority
  source: TicketSource
  language?: string
  queueId?: string
  transactions: Transaction[]
  currentTransaction: number
  issuedAt: string
  firstCalledAt?: string
  lastCalledAt?: string
  closedAt?: string
  calledToCounterId?: string
  recallCount: number
  loopCount: number
  /** Times a desk sent this visit back to its queue (at most 3). */
  backToQueueCount?: number
  /** Who the desk says the customer is; staff screens only. Mobile is E.164. */
  customerName?: string
  customerPhone?: string
  customerRef?: string
  /** The declared values this ticket carries, unfiltered — mirrors
   *  queue.Ticket.Fields. A caller showing one of these to somebody other than the
   *  one person this ticket belongs to has to filter it first; nothing here does
   *  that for you. */
  fields?: Record<string, string>
  /** The short operator remark that travels with this visit — what the greeter at
   *  the door wants the clerk at the desk to know before the customer arrives.
   *
   *  Free text, at most 255 characters, and gone the moment the visit closes: the
   *  server deletes it in the same transaction as the close, so a closed ticket
   *  never carries one. That is why it is safe to have at all, and it is why no
   *  screen should keep a copy of it anywhere the server cannot reach.
   *
   *  Absent from everything an unattended screen is sent — a hall display, a
   *  kiosk, a printed slip — by construction rather than by this comment: see
   *  ticketsForScreen and printableTicket in the Go. Do not draw it on any of
   *  them. */
  note?: string
  /** How many entries the visit's note history holds; note is the latest one's text. */
  noteCount?: number
  /** Which pool holds this customer, when they are parked. Empty on a ticket that
   *  is not parked, and on one set aside for nobody in particular. */
  parkScope?: HeldScope
  parkReason?: string
  /** When they stepped aside. This, and not issuedAt, is the wait the server calls
   *  a pooled customer by: somebody parked two minutes ago has not been waiting an
   *  hour, however long ago they arrived. */
  parkedAt?: string
  parkedByCounterId?: string
  /** The desk holding a desk-pooled customer, and the person holding a
   *  staff-pooled one. A desk that differs from parkedByCounterId means the
   *  customer was sent there by another desk, and that desk may call them whatever
   *  its profile says — the server's rule, mirrored in callableFromPool. */
  poolCounterId?: string
  poolStaffId?: string
  /** Sent to the front by a clerk: called before anybody else a desk could call,
   *  once. Absent on every other ticket. */
  front?: boolean
  /** Held back by a clerk's "after N minutes": not callable until backAt. Sent
   *  only to staff screens, and only while the hold lasts at the moment the view
   *  was built — read it through isHeld, which also asks the clock, because
   *  nothing is published at the moment a hold runs out. */
  held?: boolean
  backAt?: string
  /** The calling clock: the waiting this visit banked in lines it has since left,
   *  and when its spell in the current line began (absent when it is not waiting,
   *  and on a ticket that has never left a line, whose spell began at issue).
   *  Read them through callingWaitSeconds, which also knows a pooled customer is
   *  judged by parkedAt instead. Staff screens only; for ordering and labelling a
   *  line, never for deciding who is next — see CounterView.nextTicketId. */
  bankedWaitSec?: number
  queuedAt?: string
  /** The booking this visit arrived for, and the time it was booked for, copied at
   *  arrival. The calling clock starts at the later of this and the door. */
  appointmentId?: string
  appointmentAt?: string
  /** The staff member a booked customer is to meet, copied at arrival. */
  primaryStaffId?: string
  /** Who this customer goes back to when the work they were sent to ends —
   *  written by a transfer with Come back to me. Staff screens only. */
  return?: TicketReturn
  /** Parked into a person's pool by a return: shown as Back. */
  returned?: boolean
  /** Who sent this customer on (a transfer, or a send to a desk or person), until
   *  that work ends. Staff screens only. */
  transferredFrom?: TransferOrigin
  serviceLevelSec?: number
  /** The services this visit still owes the customer, in the order they will be
   *  taken — and not including the one in progress, which is the current
   *  transaction.
   *
   *  Absent on an ordinary single-service visit, which is almost all of them. It
   *  is what is *left* rather than what was asked for: the head of it is consumed
   *  every time a piece of work closes, so a visit issued for three services and
   *  half done carries one entry. What was asked for is in the event log, which
   *  cannot be rewritten. */
  plannedServices?: string[]
}

export interface TicketReturn {
  staffId: string
  counterId: string
  serviceId: string
}

/** The desk and person whose transfer or send put a customer where they are now. */
export interface TransferOrigin {
  counterId?: string
  staffId?: string
  at: string
}

/** A customer the person at a desk sent on with Come back to me. */
export interface AwayView {
  ticketId: string
  displayNumber: string
  serviceId: string
  state: TicketState
}

/** A booking made elsewhere: what reception arrives from, and what the counter's
 *  line marks a ticket with. Mirrors the server's bookingView (internal/api/
 *  appointments.go) — the whole booking, values included, because reception
 *  searches this list by the name or the code the customer in front of it gives.
 *  The kiosk and the counter never see this shape; they see the narrower views
 *  those routes send. */
export type AppointmentState = 'booked' | 'arrived' | 'cancelled' | 'missed'

/** The organisation's check-in window (booking.Policy), as the desk sees it. */
export interface CheckInRules {
  /** Check-in opens this many minutes before the booked time. */
  earlyMin: number
  /** Within ± this many minutes counts as on time. */
  onTimeMin: number
  /** Check-in is allowed up to this many minutes late; later, only a regular ticket. */
  lateMin: number
  /** Checked-in booked customers are called before walk-ins from their booked time. */
  bookedFirst: boolean
}

export interface Appointment {
  id: string
  tenantId: string
  branchId: string
  /** The booking system's own key. Sent back only to the caller that named the
   *  booking by it in the first place — never to a desk arriving by a code. */
  reference: string
  /** The code printed on the customer's confirmation, which is what arrives it. */
  code: string
  serviceId: string
  startsAt: string
  businessDate: string
  state: AppointmentState
  ticketId?: string
  displayNumber?: string
  arrivedAt?: string
  cancelledAt?: string
  /** Why it was cancelled, when staff or a trusted key said. */
  cancelReason?: string
  /** The declared values this booking carries — a name, a phone, most often — in
   *  the same shape a ticket's own fields are: unfiltered, so a caller showing one
   *  of these to somebody has to filter it first. */
  fields?: Record<string, string>
  /** import, online or reception. */
  source?: 'import' | 'online' | 'reception'
  /** The key's name, "Test page" or "Reception". */
  client?: string
  customerName?: string
  /** Masked (*****2D) unless the reader holds appointment.manage. */
  customerFin?: string
  channel?: BookingChannel
  contact?: string
  /** The mobile and e-mail the customer gave; the contact is one of them. */
  customerMobile?: string
  customerEmail?: string
  endsAt?: string
}

export type BookingChannel = 'sms' | 'email'

/** A branch New booking may book at, with its bookable services (GET /appointments/catalogue). */
export interface CatalogueEntry {
  branch: { id: string; name: string; timezone: string }
  services: { id: string; code: string; name: Record<string, string>; slotMin: number }[]
}
export type FinMode = 'off' | 'optional' | 'required'
/** Whether a booking asks a customer field (booking.FieldMode). */
export type FieldMode = FinMode

/** Each customer field's mode, as the Rules save them; extra is own fields by key. */
export interface CustomerFields {
  name: FieldMode
  mobile: FieldMode
  email: FieldMode
  fin: FieldMode
  extra: Record<string, FieldMode>
}

/** One own field as a booking form shows it (GET …/booking/form). */
export interface BookingFormField {
  key: string
  /** In the reader's language. */
  name: string
  type: FieldType
  mode: FieldMode
  choices?: string[]
  maxLen?: number
}

/** What a booking form asks (GET /branches/{id}/booking/form; also on …/booking/slots). */
export interface BookingFormFields {
  name: FieldMode
  mobile: FieldMode
  email: FieldMode
  fin: FieldMode
  extra: BookingFormField[]
}

/** GET /branches/{id}/booking/form: the staff booking form and the rules it keeps. */
export interface BookingForm {
  finMode: FinMode
  /** Channels a code can go by: configured, and whose field is not off. */
  channels: BookingChannel[]
  fields: BookingFormFields
  checkIn: CheckInRules
  /** Customers change their own booking until this many hours before; 0 is the minimum notice. */
  changeUntilHours: number
}

/** An organisation's online booking rules (booking.Policy). */
export interface BookingPolicy {
  fin: FinMode
  /** Minute of the day before; null is off. */
  remindDayBeforeAt: number | null
  /** Minutes before; null is off. */
  remindBeforeMin: number | null
  maxOpenPerContact: number
  /** Live holds one needs-code key may have at one branch. */
  maxHoldsPerKey: number
  manageUrl: string
  testPage: boolean
  /** The test page's address, read only; never sent back. */
  testPageUrl?: string
  /** When an open test page closes by itself, read only; never sent back. */
  testPageUntil?: string
  /** Check-in opens this many minutes before the booked time. */
  checkInEarlyMin: number
  /** Within ± this many minutes counts as on time. */
  onTimeMin: number
  /** Check-in is allowed up to this many minutes late; later, only a regular ticket. */
  lateMin: number
  /** Checked-in booked customers are called before walk-ins from their booked time. */
  bookedFirst: boolean
  /** Customers change or cancel their own booking until this many hours before; 0 is the minimum notice. */
  changeUntilHours: number
  /** Missed bookings within noShowDays that stop a contact booking online for noShowBlockDays; 0 is off. */
  noShowLimit: number
  noShowDays: number
  noShowBlockDays: number
  fields: CustomerFields
}

/** The organisation's check-in window (booking.Policy), as the desk sees it. */
export interface CheckInRules {
  /** Check-in opens this many minutes before the booked time. */
  earlyMin: number
  /** Within ± this many minutes counts as on time. */
  onTimeMin: number
  /** Check-in is allowed up to this many minutes late; later, only a regular ticket. */
  lateMin: number
  /** Checked-in booked customers are called before walk-ins from their booked time. */
  bookedFirst: boolean
}

export interface BookingBranchSettings {
  enabled: boolean
  daysAhead: number
  minNoticeMin: number
  address: string
  /** The times bookings are made for; null follows the opening hours. */
  hours: DayHours[] | null
}

export interface BookingServiceRow {
  serviceId: string
  enabled: boolean
  slotMin: number
  perSlot: number
  /** Weekdays it can be booked on, Sunday 0. */
  weekdays: number[]
  /** Bookings a day; null is no cap. */
  dailyMax: number | null
  /** Its own; null uses the branch's. */
  daysAhead: number | null
  minNoticeMin: number | null
  /** Active and routed at this branch: only these can be switched on. */
  offered: boolean
  name?: Record<string, string | undefined>
  code?: string
}

export interface ClosedDay {
  day: string
  label: string
  /** Bookings already on it, which closing it does not cancel. */
  booked: number
}

export interface BookingBranchView {
  settings: BookingBranchSettings
  services: BookingServiceRow[]
  closed: ClosedDay[]
  seat: boolean
}

export interface BookingKey {
  id: string
  name: string
  trusted: boolean
  perMinute: number
  active: boolean
  lastUsedAt?: string
  createdAt: string
}

export interface BookingSlot {
  start: string
  end: string
  free: number
}

/** A booking projected the way arriving, cancelling or saving one answers it — the
 *  server's bookingView (internal/api/appointments.go), field for field. No
 *  declared values: a caller that arrived or cancelled a booking, or the system
 *  that saved it, is not asking to be told a name back. The reference is present
 *  only when the caller named the booking by it in the first place (cancel, save
 *  — never arrive, which may have been asked for by a code alone). */
export interface BookingView {
  id: string
  branchId: string
  reference?: string
  serviceId: string
  startsAt: string
  businessDate: string
  state: AppointmentState
  ticketId?: string
  displayNumber?: string
  arrivedAt?: string
  cancelledAt?: string
  /** Why it was cancelled, when staff or a trusted key said. */
  cancelReason?: string
}

/** One row of an import, said back the way the file that sent it can act on. */
export interface ImportDecision {
  line: number
  reference: string
  outcome: 'created' | 'updated' | 'unchanged' | 'refused'
  reason?: string
  arg?: string
}

/** A row an import could not turn into a booking at all — no service, no code,
 *  something the file itself got wrong, rather than something the store refused. */
export interface ImportProblem {
  line: number
  reference?: string
  reason: string
  arg?: string
}

export interface ImportAnswer {
  rows: ImportDecision[]
  problems: ImportProblem[]
  created: number
  updated: number
  unchanged: number
  refused: number
}

/** Who may bring a parked customer back.
 *
 *  'desk' is the narrower promise — the clerk said "wait there, I will finish your
 *  case myself" — and nothing widens it to the branch. 'branch' is any desk whose
 *  work profile draws from that pool. */
export const PARK_SCOPES = ['desk', 'branch'] as const

/** A pool a work profile may draw from. */
export type ParkScope = (typeof PARK_SCOPES)[number]

/** A pool a ticket may be held in: the two a profile draws from, and the staff
 *  pool — a customer waiting for a named colleague, at whichever desk that person
 *  is signed in at. No profile draws from it; the person is who may call them. */
export type HeldScope = ParkScope | 'staff'

export type CounterState = 'open' | 'paused' | 'closed'

/** What an operator is asking for: a break or to close the desk. Mirrors
 *  org.RequestKind. */
export type RequestKind = 'lunch' | 'short_break' | 'task' | 'close'

/** Where a request stands. Mirrors org.RequestState. `auto` is a kind the branch
 *  does not require approval for, started with nobody deciding. */
export type RequestState = 'pending' | 'approved' | 'declined' | 'cancelled' | 'auto'

/** What the operator sees on the Counter page, read off the desk's state and its
 *  open request together — never the stored CounterState directly. Mirrors
 *  org.DeskStatus. `break` is a pause from before this existed, or one left by a
 *  path that pauses without a request. */
export type DeskStatus = 'ready' | 'serving' | 'pending' | 'lunch' | 'short_break' | 'task' | 'closed' | 'break'

/** A desk's open request — undecided, or a break still running. Mirrors
 *  engine.RequestView. */
export interface RequestView {
  id: string
  kind: RequestKind
  state: RequestState
  requestedAt: string
  /** Set once the break it names has started: on an approval, or at once for a
   *  kind the branch does not require approval for. */
  startedAt?: string
  /** The break reason's name, where the break was asked with one. */
  reason?: string
}

/** The session's latest request, when a manager declined it: what the operator
 *  is shown until they ask for something again. Mirrors engine.DeclinedView. */
export interface DeclinedView {
  kind: RequestKind
  reason?: string
  decidedAt?: string
}

/** The part of a branch's approval settings a desk needs to offer its requests:
 *  which kinds wait for a decision. Mirrors engine.ApprovalView. */
export interface ApprovalView {
  needsApproval: RequestKind[]
}

/** A branch's whole approval settings, as Settings › Approvals reads and saves
 *  them: who is told, and which kinds wait for a decision. Mirrors org.ApprovalSettings. */
export interface ApprovalSettings {
  emails: string[]
  needsApproval: RequestKind[]
}

/** One undecided request in a branch, with what the Floor's Requests list shows
 *  beside it: the desk and the person. Mirrors engine.PendingRequestView. */
export interface PendingRequestView {
  id: string
  counterId: string
  counterCode: string
  staffId: string
  staffName: string
  kind: RequestKind
  requestedAt: string
}

export interface CounterView {
  counterId: string
  code: string
  name: string
  state: CounterState
  /** Which rule of the work profile chose the current call (0 = Finally); null for cherry-pick, VIP and walk-in. */
  chosenBy?: { ruleId: string; rule: number } | null
  /** The last mid-session change of work profile. */
  profileChangedBy?: { staffId: string; staffName: string; at: string } | null
  /** Read off state, the customer at the desk and the open request below, by the
   *  server's org.DeriveStatus. Absent from a server too old to send it. */
  status?: DeskStatus
  /** The desk's open request — undecided, or a break still running — absent when
   *  it has none. */
  request?: RequestView
  /** The session's latest request, shown only when a manager declined it. */
  declined?: DeclinedView
  sessionId?: string
  staffId?: string
  staffName?: string
  workProfileId?: string
  profileName?: string
  ticketId?: string
  displayNumber?: string
  ticketState?: TicketState
  pauseReason?: string
  sortOrder: number
  /** Parked customers waiting for this desk by name: sent to it, parked by it, or
   *  waiting for the person signed in at it. Absent when there are none. */
  held?: number
  /** What this service point withholds, after the branch's overrides. Absent or empty: everything allowed. */
  withheld?: ServicePointVerb[]
  /** When the desk asked for its next customer to be called automatically. Absent when it has not. */
  standingBySince?: string
  /** When the person at this desk signed in. Absent on a closed desk and on a hall board. */
  openedAt?: string
  /** When the break this desk is on began. Absent when it is not paused. */
  pausedSince?: string
  /** The customer Call next would take at this desk, chosen by the server with the
   *  same selection the call makes. Absent when nobody is signed in or nobody is
   *  callable. It is right as of the message it came on: a hold or a cooldown
   *  running out publishes nothing, and the call itself decides afresh. */
  nextTicketId?: string
  /** When a customer this desk could call comes out of a recycle cooldown or a
   *  clerk's delay, which can change nextTicketId with nothing published. The
   *  counter asks for a fresh snapshot once it passes — see lib/nextHere. */
  nextCheckAt?: string
  /** The customers the person here sent on with Come back to me, not back yet. */
  away?: AwayView[]
  /** Simple mode: the queues picked at the desk (no work profile). */
  queueIds?: string[]
  /** A supervisor's change waiting for the visit at the desk to end. */
  pending?: PendingSelection | null
}

/** One of the organisation's break reasons. Mirrors org.BreakReason. */
export interface BreakReason {
  id: string
  name: string
  kind: Exclude<RequestKind, 'close'>
  sortOrder: number
}

/** The organisation's desk rules. Mirrors org.CounterPolicy. */
export interface CounterPolicy {
  breakApproval: boolean
  outOfTurnReasonRequired: boolean
}

/** A supervisor's change waiting for the desk's visit to end. */
export interface PendingSelection {
  workProfileId?: string
  queueIds?: string[]
  byId?: string
  at?: string
}

export interface QueueView {
  queueId: string
  code: string
  name: Record<string, string>
  /** What this queue's tickets start with. */
  letter?: string
  waiting: number
  longestWaitSec: number
  breaching: number
  sortOrder: number
  /** The services that route into this line, on the floor.
   *
   *  What a counter reads to say where a customer goes when the service they are
   *  having ends and the next planned one begins. It is here, on the live
   *  snapshot, rather than in GET /branches/{id}/queues, which answers from the
   *  draft and belongs to the administration screens — see engine.QueueView.
   *
   *  Absent on a hall board and a kiosk, which have theirs stripped, and on a
   *  queue nothing routes to. */
  serviceIds?: string[]
  /** The queue's own target; absent when each service keeps its own. */
  serviceLevelSec?: number
  /** What a customer taking a standard ticket here now would be told; on the
   *  snapshot only (deltas do not carry it), so GET /estimates keeps it fresh. */
  newCustomer?: QueueEstimate
}

export interface Service {
  id: string
  code: string
  name: Record<string, string>
  avgServiceSec: number
  sortOrder: number
  active: boolean
  /** The slip this service prints, unless a machine has one of its own. Absent
   *  prints what the branch prints. */
  ticketLayoutId?: string
  /** Where the counter's Send to cashier button sends. */
  cashier?: boolean
  /** Minutes one piece of work may run before the desk turns amber and the
   *  managers are emailed. Absent or 0: no limit. */
  serviceTimeLimitMin?: number
}

/**
 * The screen app a machine runs, as the layout answer names it.
 *
 * `url` is the entry file, served by this machine's own address with the version
 * in the path, so it caches for ever and a rollback is a new url. `attributes` are
 * this placement's values, already reconciled on the server against the manifest
 * that is current. `fields` and `connectors` are what the manifest declares; the
 * server gates both again, so they are here to be read, not trusted.
 */
export interface ScreenAppAnswer {
  id: string
  name: string
  version: string
  versionId: string
  hostApi: number
  entry: string
  url: string
  attributes: Record<string, unknown>
  fields: string[]
  connectors: string[]
}

/** One attribute a manifest declares: what an administrator may set for each
 *  placement of the app, reconciled against the current manifest whenever it
 *  changes (screenapp.Reconcile). */
export interface ScreenAppAttribute {
  key: string
  type: 'integer' | 'string' | 'boolean'
  name: string
  default: string | number | boolean
}

/** An app's manifest.json, as the Screen apps page and the Machines page read it
 *  to build the attribute form. The server checks every value against this again
 *  at placement and at issue time; this copy is read, never trusted alone. */
export interface ScreenAppManifest {
  id: string
  name: string
  version: string
  kind: 'kiosk' | 'display'
  entry: string
  hostApi: number
  attributeSchema: number
  attributes: ScreenAppAttribute[]
  fields: string[]
  connectors: string[]
}

/** One upload: a version of an app, kept for ever so choosing it again later is a
 *  rollback rather than a re-upload. */
export interface ScreenAppVersion {
  id: string
  version: string
  manifest: ScreenAppManifest
  size: number
  sha256: string
  uploadedBy: string
  uploadedAt: string
}

/** An organisation's own kiosk or hall board, uploaded as a zip
 *  (docs/EXTENSIBILITY.md section 3). `currentVersionId` is what a machine placed
 *  on it runs; an older version stays in `versions`, so choosing it again is the
 *  rollback. */
export interface ScreenApp {
  id: string
  key: string
  name: string
  kind: 'kiosk' | 'display'
  currentVersionId: string
  createdAt: string
  retiredAt: string | null
  /** Newest first, never null. */
  versions: ScreenAppVersion[]
  /** How many active machines run it — what Retire is refused for (APP_PLACED). */
  placedOn: number
}

export interface Announcement {
  ticketId: string
  displayNumber: string
  counterId: string
  counterName: string
  repeat: boolean
  at: string
}

export interface Snapshot {
  branchId: string
  branchName: string
  businessDate: string
  version: number
  at: string
  tickets: Ticket[]
  counters: CounterView[]
  queues: QueueView[]
  services: Service[]
  /** The oldest business date still holding open tickets, when it is not today's.
   *  Absent when the branch is only carrying today — which is the ordinary case,
   *  and is why a day nobody ended is worth saying out loud. */
  unclosedFrom?: string
  /** The published version of the branch's setup everything above was read under.
   *  Optional because a browser left open across a deployment is running
   *  yesterday's page against today's server, and the reverse is just as true. */
  configVersion?: number
  /** The queues over their alert level now. Staff screens only; absent from a
   *  server older than this and from every unattended screen. */
  alerts?: QueueAlert[]
  /** What the Counter page needs to offer a request without a separate read:
   *  which kinds wait for a decision. Absent from a
   *  server too old to send it, and never repeated on a delta — a desk asked
   *  mid-session still offers what it read on connecting. */
  approval?: ApprovalView
  /** The branch's undecided desk requests, oldest first. Staff screens only;
   *  absent from a server too old to send it. A delta carries no list of its
   *  own — each desk's own `request` on its counter view is what changes, and a
   *  screen rebuilds this one from those (lib/pendingRequests). */
  requests?: PendingRequestView[]
  /** The booking policy's booked-first rule, so lines are drawn in Call next's order. Staff screens only. */
  bookedFirst?: boolean
  /** Today's latest calls, newest first, ended visits included; absent from an older server. */
  calls?: CallView[]
}

/** One ticket's latest call (engine.CallView). */
export interface CallView {
  ticketId: string
  displayNumber: string
  counterId: string
  queueId?: string
  calledAt: string
}

export interface Delta {
  branchId: string
  from: number
  version: number
  at: string
  tickets?: Ticket[]
  removed?: string[]
  /** Removed tickets that also leave the boards' calls: voided, or closed by the day's end. */
  uncalled?: string[]
  /** End of day or Reset now: boards empty their calls. */
  dayEnded?: boolean
  counters?: CounterView[]
  announce?: Announcement | null
  /** Set only by a publish. A delta carries no queues, no services and no branch,
   *  so there is nothing here to patch them from: a screen that sees a version it
   *  does not hold asks for a fresh snapshot instead. */
  configVersion?: number
}

export interface Envelope {
  type: 'snapshot' | 'delta' | 'alerts' | 'error' | 'pong' | 'press' | 'layout'
  branchId?: string
  snapshot?: Snapshot
  delta?: Delta
  /** Set only on an 'alerts' message: the branch's whole list of raised queues,
   *  replacing the last one. Always present on that message, even when empty. */
  alerts?: QueueAlert[]
  error?: string
  /** Lets a screen tell one refusal from another without reading English.
   *  'in_use' is the one it must act on. */
  code?: string
  /** Set only on a 'press' message, sent to a feedback tablet's own connection
   *  when it claims its machine: what every press from it must carry. Kept in
   *  memory for as long as that connection lasts, and never stored or logged. */
  pressToken?: string
  at: string
}

/** What a kiosk's slip should look like. Decided at head office; where the paper
 *  physically comes out is a fact about the room and stays on the machine. */
export interface PrinterSettings {
  paperMm?: number
  copies?: number
  cut?: boolean
  render?: 'raster' | 'text'
}

/** What a machine says out loud when a ticket is called. On the machine rather
 *  than the branch because it is a fact about the room: the board over the counters
 *  announces, the one by the waiting area is silent. */
export interface VoiceSettings {
  /** Whether this machine makes any sound at all. */
  announce?: boolean
  /** Whether this machine reads the calls out. Which machine is the hall's voice
   *  is a fact about the room: a branch whose television has no speakers, or has
   *  it mounted above the ceiling, announces from the kiosk instead. Unset means
   *  yes on a hall board and no on a kiosk. */
  calls?: boolean
  language?: 'az' | 'en' | 'ru'
  repeat?: number
  /** Whether a call plays the chime, with or without the words. Unset follows `calls`. */
  chime?: boolean
  /** An uploaded sound played as the chime; unset is the built-in tone. */
  chimeAsset?: string
}

/** Whether a hall board gives a fresh call the whole screen, and for how long.
 *  Off unless set. The queue rules are lib/callPage.ts's, not settings. */
export interface CallPageSettings {
  show?: boolean
  /** 3 to 30; absent is 8. */
  seconds?: number
}

/** One machine standing in a branch: the kiosk in the lobby, the board over the
 *  counters. Its code is what appears in the URL — /display/BAKU-01/TV1. */
export interface Unit {
  id: string
  tenantId?: string
  branchId: string
  code: string
  /** A machine is a kiosk, a display, a desk display or a feedback tablet. The
   *  server also accepts "board", which is what a display was called for a few
   *  hours on 17 September 2026. A desk display runs as a display for the desks in
   *  `desks`; a feedback tablet stands at exactly one desk, in `desks`. */
  kind: 'kiosk' | 'display' | 'desk_display' | 'feedback'
  name: string
  printer: PrinterSettings
  /** Which of the organisation's ticket designs this machine prints, overriding
   *  its branch. Empty follows the branch, which is what almost every kiosk does.
   *  Refused on a hall display, which prints nothing. */
  ticketLayoutId?: string
  /** This machine's own screen design (a display design on a display, a kiosk
   *  design on a kiosk), overriding its branch. Empty follows the branch. Sent by
   *  servers from 0062 on; absent from an older one. */
  layoutId?: string
  voice: VoiceSettings
  /** The desks this board shows the calls for. Empty means all of them. Only
   *  meaningful on a hall board — a kiosk shows nobody's calls. */
  desks: string[]
  /** Only on a hall board: a kiosk shows nobody's calls. */
  callPage?: CallPageSettings
  active: boolean
  /** Whether a machine is holding this unit right now. Live, not stored. */
  running?: boolean
  /** What the machine last said it has drawn, while it is connected and only on a
   *  branch's machine list. designBehind: not the design or version published for
   *  it. appBehind: drawn with the app from before the last deploy. */
  showing?: { layoutId: string; version: number; build?: string; at: string; designBehind: boolean; appBehind: boolean }
  /** What a kiosk last said of its printer since the server started; only on a
   *  branch's machine list. errorAt is its last failure. */
  printerState?: { state: 'ready' | 'faulty' | 'absent'; error?: string; errorAt?: string; at: string }
  /** The last playlist item a hall TV said it could not play, since the server started. */
  mediaSkipped?: MediaSkip
  /** What a screen's worker last said it holds, since the server started. */
  screenCache?: ScreenCache
  /** The uploaded screen app this machine runs instead of its design; absent runs
   *  the design. Set together with `appAttributes` through PUT /units/{id}/app,
   *  never through PATCH /units/{id} — placing an app reaches the running machine
   *  at once, which is not what the rest of this screen's Save does. See
   *  docs/EXTENSIBILITY.md section 3. */
  appId?: string
  /** This placement's own values for the app's attributes, reconciled against its
   *  current manifest by the server. Absent when no app is placed. */
  appAttributes?: Record<string, unknown>
}

/** What an unattended screen is told about itself when it is switched on. */
export interface ScreenView {
  branch: {
    id: string
    code: string
    name: string
    timezone?: string
    openNow: boolean
    opensAt?: string
    /** When an open branch shuts. */
    closesAt?: string
  }
  unit: Unit
  /** A kiosk's menu. A hall board is sent none. */
  services?: Service[]
  /** A feedback tablet's buttons; nobody else is sent any. */
  marks?: TabletButton[]
  /** The organisation's customer languages, in order; the first is the default. */
  languages?: LanguageCode[]
}

/** A customer mark as a feedback tablet draws it: what it sends back when
 *  pressed, and the words on it. Already chosen and ordered by the server
 *  (tabletButtons in internal/api/screen.go); the score and the flags stay there. */
export interface TabletButton {
  id: string
  code: string
  name: Record<string, string>
}

export interface Staff {
  id: string
  tenantId: string
  username: string
  displayName: string
  /** The organisation role this person holds. Empty only for the installation's
   *  owner, who belongs to no organisation and holds no role. */
  roleId?: string
  /** That role's name, kept here so it still reads after the role is renamed or
   *  deleted. */
  roleName?: string
  roleKey?: string
  /** This person's own changes to their role's permissions. */
  added?: Permission[]
  removed?: Permission[]
  /** True for the installation's owner: no organisation, no role, everything. */
  owner?: boolean
  /** The branches this person works in. Absent or empty means every branch in
   *  their organisation, which is what an administrator has; the server refuses to
   *  save an operator that way. */
  branchIds?: string[]
  active: boolean
  mustChangePassword?: boolean
  /** Who checks this person's password: this server, or the organisation's
   *  directory. Absent on a row from before this existed means "local". */
  signIn?: 'local' | 'directory' | 'sso'
  /** Their own choice; empty follows the organisation's. */
  language?: StaffLanguage | ''
}

/**
 * What a person may do, named by the server.
 *
 * The apps hide what the caller cannot use, and they take the list from the server
 * rather than inferring it from the role. An app that guesses at what "supervisor"
 * means will eventually guess differently from the server, and the difference shows
 * up as a button that does nothing.
 */
export type Permission =
  | 'branch.read'
  | 'ticket.read'
  | 'ticket.issue'
  | 'ticket.priority'
  | 'counter.operate'
  | 'ticket.supervise'
  | 'report.read'
  | 'audit.read'
  | 'appointment.manage'
  | 'booking.manage'
  | 'catalogue.manage'
  | 'profiles.manage'
  | 'branch.manage'
  | 'layout.manage'
  | 'signage.manage'
  | 'staff.manage'
  | 'tenant.manage'
  | 'licence.read'
  | 'licence.manage'
  | 'settings.manage'
  | 'status.approve'
  | 'ticket.vip'

export interface Identity {
  /** The organisation's own address when the server gives each one; a sign-in at
   *  the central address goes on there. */
  home?: string
  staff: Staff
  permissions: Permission[]
  /** Repeated from the staff object, at the top where the menu reads them. */
  roleName?: string
  roleKey?: string
  owner?: boolean
  organisation?: string
  mustChangePassword?: boolean
  /** The optional modules this installation's licence includes. Taken from the
   *  server rather than guessed, for the same reason the permissions are: an app
   *  that decides for itself what a licence covers will eventually decide
   *  differently, and the difference is a menu item that answers with a refusal. */
  modules?: string[]
  /** The delivered modules the licence's own document grants, sent even while a
   *  lock empties `modules` for everything. Lets a screen that must still offer
   *  a locked module's own switch-off or delete controls (Online booking) tell
   *  "bought, but locked" apart from "never bought" — which `modules` alone
   *  cannot, since a lock empties it the same way for both. */
  boughtModules?: string[]
  /** Who grants the modules: the organisation's plan (saas) or the licence file (onprem). */
  deployment?: 'onprem' | 'saas'
  /** Where that plan or licence stands; 'expired' is an ended plan on saas. */
  licenceState?: LicenceState
  /** Where "Sign out" must send the browser when a proxy signed this session in:
   *  clearing our own cookie alone would be undone by the very next request, since
   *  the proxy would sign the same person straight back in. Absent for an
   *  ordinary sign-in, which "Sign out" ends here. */
  signOutUrl?: string
  /** What this person's screens read in: their own choice, else their
   *  organisation's, else English. Worked out by the server, not guessed here. */
  language: StaffLanguage
  /** The organisation's default, so the Account screen can say what "follow my
   *  organisation" would mean. Absent for the installation's owner, who belongs
   *  to none. */
  organisationLanguage?: StaffLanguage
  /** The organisation's staff default and customer languages (GET /organisation/languages);
   *  English alone for the installation owner. Absent from a server older than 31 Oct 2026. */
  languages?: LanguageSettings
  /** On a branch server (edge): the server its setup is changed at; writes here are refused. */
  setupAt?: string
  /** Organisation options as the screens obey them (chosen and licensed), by key:
   *  handoff, outOfTurnCall, onlineBooking, priorityTickets. A missing key is off. */
  options?: Record<string, boolean>
  /** The hand-off button's name chosen by the organisation; absent = the phrase. */
  handoffLabel?: string
  /** The access token this request was made with; null for a browser session. */
  viaToken?: ViaToken | null
  /** The organisation's own words (Settings › Wording): lang → key → text. */
  wording?: Wording
}

/** Names the access token a request was made with (never the secret). */
/** One entry of a ticket's history (GET /tickets/{ticketId}/events). */
export interface TicketHistoryEvent {
  seq: number
  kind: string
  at: string
  actorId?: string
  actor?: string
  payload?: Record<string, unknown>
  /** The access token the action was taken with; null for a session. */
  viaToken: Pick<ViaToken, 'id' | 'name'> | null
}

export interface ViaToken {
  id: string
  name: string
  /** The secret's last four characters. */
  hint: string
}

/** A personal access token (Authorization: Bearer qms_pat_…). The secret is shown once, at creation. */
export interface AccessToken {
  id: string
  name: string
  /** The secret's last four characters. */
  hint: string
  createdAt: string
  expiresAt: string | null
  lastUsedAt: string | null
  lastUsedIp: string | null
  revokedAt: string | null
}

/** Which token a request or an audit event came through. */
export type AccessTokenRef = ViaToken

/** One row of what a directory group gives: a role, or a branch, never both.
 *  `position` is the order role mappings are tried in — the first matching one
 *  gives the role, since roles have no rank of their own any more. It plays no
 *  part among branch mappings, which are always combined. */
export interface GroupMapping {
  id: string
  group: string
  roleId?: string
  branchId?: string
  /** Always sent by the server (org.GroupMapping.Position); optional only so a
   *  row this form is still building, before a save round-trips, need not
   *  invent one. */
  position?: number
}

/** One of an organisation's own roles: a name and the permissions it gives.
 *  Every organisation starts with five (Admin and four others); any but the
 *  locked one (Admin) may be renamed, changed or deleted. */
export interface OrgRole {
  id: string
  name: string
  /** The starting role this still is by name; empty for a role of the organisation's own. */
  roleKey?: string
  locked: boolean
  permissions: Permission[]
  /** How many people hold the role, filled by the list that shows it. */
  holders: number
}

/** One tick box in the role editor: a screen and the permissions it grants or
 *  withholds together. Mirrors org.Screen. */
export interface Screen {
  key: string
  group: 'daily' | 'manage' | 'settings'
  permissions: Permission[]
  /** The licence module this screen belongs to, absent for the core. */
  module?: string
}

/** An organisation's LDAP or Active Directory connection, as the Directory screen
 *  reads and saves it. The bind password never round-trips — see
 *  bindPasswordSet — because it is sealed on the server and never leaves it. */
export interface DirectorySettings {
  url: string
  startTls: boolean
  caPem?: string
  bindDn?: string
  userTemplate?: string
  baseDn: string
  userFilter: string
  displayNameAttr: string
  groupAttr: string
  nestedGroups: boolean
  createAccounts: boolean
  offlineGraceHours: number
  enabled: boolean
  updatedAt: string
  /** True once a service account's password has been saved. The password itself
   *  is never sent back. */
  bindPasswordSet: boolean
  /** True when this server's key cannot open the saved password — most often a
   *  database restored without the data directory that held the key. The screen
   *  asks for the password again rather than pretending it still works. */
  bindPasswordUnreadable?: boolean
}

/** An organisation's OpenID Connect provider as the form edits it. */
export interface SSODraft {
  displayName: string
  issuer: string
  clientId: string
  scopes: string
  usernameClaim: string
  nameClaim: string
  emailClaim: string
  groupsClaim: string
  emailDomains: string[]
  createAccounts: boolean
  enabled: boolean
}

/** The saved provider (GET /sso). The client secret never comes back. */
export interface SSOSettings extends SSODraft {
  updatedAt: string
  clientSecretSet: boolean
  clientSecretUnreadable?: boolean
}

/** What the provider's discovery document names (POST /sso/test). */
export interface SSOEndpoints {
  issuer: string
  authorizationEndpoint: string
  tokenEndpoint: string
  userinfoEndpoint?: string
  jwksUri: string
  endSessionEndpoint?: string
  algorithms: string[]
}

/** A sign-in service the sign-in page offers. */
export interface SSOChoice {
  name: string
  start: string
}

/** What trying one person against the saved directory answered: the Directory
 *  screen's own Test card, and never an account created or changed by it. */
export interface DirectoryTestResult {
  person: { dn: string; displayName: string; groups: string[] }
  roleId?: string
  roleName?: string
  roleKey?: string
  branchIds?: string[]
  /** Set instead of roleId/branchIds when the directory accepted the password but
   *  none of the person's groups is mapped here. */
  refusal?: string
}

/** A person as the administration screens see them. */
export interface StaffMember extends Staff {
  /** What this installation's policy asks of this role. The screen cannot work it
   *  out: the policy is an installation setting an ordinary administrator may not
   *  read, and they are the person who needs to see which accounts are exposed. */

  /** True for an account that administers the server rather than this
   *  organisation — the supplier's. Listed here on purpose: a customer who finds
   *  out about it after go-live reacts far worse than one told on the first day. */
  installation?: boolean
  /** False for a row this person may look at and not change. */
  editable: boolean
  /** From the account policy: why the right password would still be refused.
   *  Absent while the policy is off. */
  standing?: 'locked' | 'dormant'
  /** What this person may do in the end: their role, plus what was added for
   *  them, minus what was removed. */
  permissions: Permission[]
}

/** One entry on the sign-in screen's organisation list: enough to choose, and no
 *  more. On-premise only — the hosted service never lists its customers. */
export interface OrganisationChoice {
  slug: string
  name: string
  /** This organisation's default staff language, so the sign-in screen can read
   *  in it before anybody has signed in. */
  language?: StaffLanguage
}

/** An organisation as the installation owner administers it. */
export interface Organisation {
  id: string
  name: string
  slug: string
  hostname?: string
  timezone: string
  /** The default language this organisation's staff screens read in for anybody
   *  who has not chosen their own. Chosen when the organisation is created, then
   *  changed on the People screen. */
  staffLanguage?: StaffLanguage
  active: boolean
  createdAt: string
}

/** One weekday's opening, in minutes from local midnight. */
export interface DayHours {
  day: number
  open: number
  close: number
}

export interface Branch {
  id: string
  tenantId?: string
  code: string
  name: string
  timezone: string
  rolloverMinute: number
  /** Minutes after closing at which the day ends by itself; 0 is at closing. */
  autoEndOfDayMin?: number
  /** True when the day never ends by itself (default false: ends at closing). */
  noAutoEndOfDay?: boolean
  /** When the branch is open. An empty list means nobody has said, which is not the
   *  same as closed every day — a branch that has not said is treated as open. */
  openingHours: DayHours[]
  /** The published profile pointers; absent before the first publish. */
  operationProfileId?: string
  equipmentProfileId?: string
  active: boolean
  /** Whether the branch is open right now, decided by the server. A lobby machine's
   *  clock is the one clock in the building nobody checks. */
  openNow?: boolean
  opensAt?: string
  closesAt?: string
}

export interface WorkProfile {
  id: string
  name: string
  rule: string
  /** The work profile's id in its operation profile, and its chain of calling rules. */
  templateId?: string
  rules?: CallRule[]
  wrapUpSec: number
  noShowSec: number
  recallIntervalSec: number
  /** Announce a called customer again every this many seconds. Absent or 0: never. */
  repeatCallSec?: number
  queueIds: string[]
  /** The pools this profile calls parked customers out of. Absent or empty means
   *  parked customers stay parked until somebody fetches them by hand, which is
   *  what every profile did before pools existed. */
  poolScopes?: ParkScope[]
  /** How much of this desk's calling each queue gets under fair_share, and nothing
   *  at all under any other rule. A queue this profile serves and this does not
   *  name has a share of one — see shareOf in lib/shares.
   *
   *  Kept by the server whatever rule the profile is on, so an administrator who
   *  sets a ratio, tries strict priority for a morning and comes back still has
   *  their numbers. */
  shares?: Record<string, number>
  /** The exceptions to the rule, checked in order before it, the first that is
   *  true deciding — see lib/conditions. Absent when there are none, which is how
   *  the server writes a profile with no exceptions. */
  conditions?: CallCondition[]
  /** Only sent by the administration listing. The counter screen never sees a
   *  retired profile at all, so it has no use for the field. */
  active?: boolean
  /** Absent or empty: everything allowed, which is every profile or desk that nobody has switched. */
  withheld?: ProfileVerb[]
  /** How long before their booked time a booked customer may be called at a desk
   *  on this profile, in seconds. Absent is any time, which is how the server
   *  writes a profile with no limit; null is how a save says "any time again". */
  appointmentEarlySec?: number | null
}

/** Something a work profile may switch off. The job, not the window: see
 *  internal/queue/verbs.go. The verbs that end a visit are never here. */
export type ProfileVerb = 'transfer' | 'send' | 'park' | 'recall'
/** Something a desk may switch off because of where it is: see
 *  internal/org/deskverbs.go. */
export type DeskVerb = 'walk_direct' | 'quick_serve'

/** The five rules a desk can call by, in the order the administration screen
 *  offers them: the plain one first, the clever ones after. */
export const CALLING_RULES = [
  'longest_waiting',
  'priority_then_waiting',
  'service_level_risk',
  'queue_order',
  'fair_share',
] as const

export type CallingRule = (typeof CALLING_RULES)[number]

/** The five things an exception to a calling rule can watch: how long the person
 *  at the front of a queue has waited, how many are waiting in it, what the clock
 *  on the wall says, how far past the target anybody there has gone, and whether a
 *  booked customer has been kept past their time. */
export const CONDITION_KINDS = [
  'wait_over',
  'length_over',
  'time_between',
  'over_service_level',
  'appointment_late',
] as const

export type ConditionKind = (typeof CONDITION_KINDS)[number]

/** One "except when" on a work profile, as queue.CallCondition writes it — all
 *  five keys, always, in this order. threshold is seconds for a wait and for a
 *  wait past the target, people for a length, and 0 for a time of day; from and
 *  until are a time of day's window in minutes past midnight, and 0 otherwise. */
export interface CallCondition {
  kind: ConditionKind
  queueId: string
  threshold: number
  from: number
  until: number
}

/** One ticket letter's numbers at a branch, as queue.NumberRange writes it. */
export interface NumberRange {
  prefix: string
  first: number
  last: number
}

/** A line inside one branch, and the services routed into it. */
export interface Queue {
  id: string
  tenantId: string
  branchId: string
  code: string
  name: Record<string, string>
  serviceLevelSec: number
  sortOrder: number
  active: boolean
  serviceIds: string[]
  /** The queue's id in its operation profile, its letter and number range, and its kind. */
  templateId?: string
  letter?: string
  firstNumber?: number
  lastNumber?: number
  type?: QueueType
}

/**
 * One routing rule on a branch: visits that match it wait in `queueId` instead of
 * in their service's own line. The mirror of internal/catalog.Segment.
 *
 * Each dimension is a set, and an absent one means any value. The server leaves
 * an empty set out of what it sends, and lib/segments sends it back the same way,
 * so that a rule nobody has touched never reads as edited.
 */
export interface Segment {
  /** Absent on a rule the screen has just made; the server gives it one. */
  id?: string
  name: string
  serviceIds?: string[]
  priorities?: Priority[]
  languages?: string[]
  sources?: TicketSource[]
  queueId: string
}

/** The figures a branch manager is measured on.
 *
 *  The definitions live on the server, in internal/report, and are stated there
 *  rather than here: waiting is issue to first call, handling is per piece of work,
 *  and the no-show rate is measured against tickets that were called. */
export interface Durations {
  count: number
  meanSec: number
  medianSec: number
  p90Sec: number
  maxSec: number
}

export interface ReportCounts {
  issued: number
  served: number
  /** Called at least once and nobody came forward — an event, so a no-show later
   *  served is here and in served. */
  noShow: number
  /** Voided by the staff. A customer who left from their phone is in left. */
  voided: number
  left: number
  /** Closed with no work done and no call missed. */
  unserved: number
  stillOpen: number
  /** Called again at least once (recall_count > 0). */
  recalled: number
  /** The same tickets, each counted once (internal/report Outcome). */
  outcome: ReportOutcome
}

export interface ReportOutcome {
  served: number
  stillOpen: number
  left: number
  noShow: number
  voided: number
  unserved: number
  /** Voided before anyone called it; absent from a server before QA-2. */
  leftBeforeCall?: number
}

export interface ServiceLevel {
  measured: number
  within: number
}

export interface ReportRow {
  key: string
  label: string
  /** A service row's name in the reader's language; label is its code. */
  name?: string
  counts: ReportCounts
  waiting: Durations
  handling: Durations
  serviceLevel: ServiceLevel
  waitBands?: Bands
  handlingBands?: Bands
}

/** Waits or handling times counted in bands (internal/report/bands.go). Band i is
 *  (edge[i−1], edge[i]], the first [0, edge[0]], the last over the last edge. */
export type Bands = {
  counts: number[]
  /** Closed or void, never called: in every "called within" denominator. */
  notCalled: number
  /** Target mode: called, but the service has no target. */
  noTarget?: number
}

/** The groupings the server answers one at a time (report/breakdown, report.Dim). */
export type FetchedDim = 'day' | 'week' | 'month' | 'weekday' | 'interval15' | 'interval30' | 'channel' | 'priority' | 'language' | 'call'

/** One pair of members of two fields crossed (report/cross, report.CrossCell): keyed
 *  and labelled as each field's own rows. Where either field is work, only Served and
 *  handling are counted. */
export interface CrossCell {
  a: string
  aLabel: string
  b: string
  bLabel: string
  counts: ReportCounts
  waiting: Durations
  handling: Durations
  serviceLevel: ServiceLevel
}

/** How the bands were cut: minutes, or percent of the service's target. */
export type BandSpec = { waitBy: 'minutes' | 'target'; waitEdges: number[]; handlingEdges: number[] }

/** A saved view's Tickets filter: the Tickets tab's query without branches and dates (report.TicketViewFilters). */
export type TicketViewFilters = {
  service?: string
  staff?: string
  result?: string
  channel?: string
  arrival?: string
  sources?: string[]
  priority?: string
  language?: string
  weekday?: number
  issuedFrom?: string
  issuedTo?: string
  call?: string
  counted?: string
  desk?: string
  outcome?: string
  mark?: string
  ended?: boolean
  booked?: string
  wait?: { by: 'minutes' | 'target'; lo: number; hi: number; notCalled?: boolean }
  /** The words the chips showed for filters that hold an id (a desk's name). */
  names?: Record<string, string>
}

/** What a saved view redraws (report.ViewSpec); period '' keeps the period chosen. */
export type ViewSpec = {
  layout?: { rows: string[]; cols: string[]; values: { measure: string; stat?: string }[] }
  period: string
  bands?: BandSpec
  visual?: string
  tickets?: TicketViewFilters
  /** A pivot report's layout (kind pivot). */
  pivot?: PivotSpec
}

export type ViewKind = 'figures' | 'tickets' | 'pivot'

/** A view to save; organisation only for the installation owner. */
export type ViewDraft = {
  name: string
  kind: ViewKind
  shared: boolean
  spec: ViewSpec
  organisation?: string
  /** The browser's own id of a view moved from it: the server keeps it once. */
  importKey?: string
  /** Pivot reports: what it is for. */
  description?: string
}

/** A saved view as the server keeps it (store.ReportView), with what the reader may do to it. */
export type ReportView = {
  id: string
  tenantId: string
  /** Absent once the owner's account is gone. */
  ownerId?: string
  ownerName?: string
  name: string
  kind: ViewKind
  shared: boolean
  spec: ViewSpec
  /** Pivot reports: what it is for, and its number in the organisation (#n). */
  description?: string
  number?: number
  createdAt: string
  updatedAt: string
  mine: boolean
  mayDelete: boolean
  /** Its owner has left, and this person looks after the organisation: they may become its owner. */
  mayTakeOver?: boolean
  /** Schedules that send it, and of those the ones its owner did not make. */
  inUse: number
  inUseByOthers: number
}

/** The booking report's groupings (report.BookingBys): hour is of the booked time. */
export type BookingBy = 'service' | 'day' | 'week' | 'weekday' | 'hour' | 'source'

/** One set of booking figures (report.BookingCounts). Arrived is early + onTime + late;
 *  pending is still booked. Lateness is arrived − booked time, negative when early. */
export interface BookingCounts {
  booked: number
  arrived: number
  early: number
  onTime: number
  late: number
  missed: number
  cancelled: number
  moved: number
  pending: number
  lateness: Durations
  leadTime: Durations
  /** Same day, next day, 2–7 days, 8+ days, by the branch's calendar. */
  lead: [number, number, number, number]
  waited: Durations
  serviceLevel: ServiceLevel
}

export interface BookingRow extends BookingCounts {
  key: string
  label: string
  /** A service row's name in the reader's language; label is its code. */
  name?: string
}

/** GET /branches/{id}/report/bookings: a branch's confirmed bookings over a period. */
export interface BookingReport extends BookingCounts {
  range: { from: string; to: string }
  by: BookingBy
  rows: BookingRow[] | null
  /** The branch business date moves are recorded from (when migration 0079 ran here); absent from an older server. */
  movesSince?: string
  /** The ± minutes early, on time and late were split by: the policy's on-time band. */
  onTimeMin?: number
}

export interface ReportSummary {
  branchId: string
  branchName: string
  range: { from: string; to: string }
  counts: ReportCounts
  waiting: Durations
  handling: Durations
  serviceLevel: ServiceLevel
  bands: BandSpec
  waitBands: Bands
  handlingBands: Bands
  byService: ReportRow[]
  /** Every piece of work counted by the service it delivered, as against
   *  byService, which attributes a whole ticket to the service the customer
   *  arrived for. On a branch with no multi-service visits the two agree row for
   *  row; where they disagree this one answers "what did we actually do today".
   *
   *  Optional for the reason byOutcome and byMark are: a browser left open across
   *  a deployment is running yesterday's page, and a server that predates this
   *  breakdown sends nothing rather than an empty list. */
  byServiceWorked?: ReportRow[]
  byCounter: ReportRow[]
  byOperator: ReportRow[]
  byHour: ReportRow[]
  /** Empty for an organisation that has defined no outcomes, which is the honest
   *  answer: an empty table says nobody has been asked to record this. */
  byOutcome?: ReportRow[]
  /** The "not recorded" row byOutcome leaves out when nothing was recorded at all, for the matrix. */
  outcomeUnrecorded?: ReportRow
  /** What else was noted, and the one breakdown here that does not partition the
   *  period. A piece of work carrying two marks is in two rows and one carrying
   *  none is in no row, so these counts do not sum to anything — whatever renders
   *  them has to say so, because a column of numbers under a heading is read as a
   *  share of a total unless it is told not to be. */
  byMark?: ReportRow[]
  /** What the desks recorded delivering, per delivered service and outcome, with
   *  Orchestra's transaction time. Optional for the reason the two above are. */
  byDelivered?: DeliveredRow[]
  /** Time at the desks, per desk and per person: signed in, paused, serving,
   *  wrap-up and idle, with the definitions in internal/report/presence.go.
   *  Optional for the reason byDelivered is. */
  byDeskTime?: TimeRow[]
  byStaffTime?: TimeRow[]
  /** Every desk_request touching the period, oldest first. Optional for the
   *  reason byDelivered is: a browser left open across a deployment, or a
   *  server from before this table existed. */
  breaks?: BreakRow[]
  /** Presses on the desks' feedback tablets, per desk, person and service. See
   *  internal/report/satisfaction.go. Optional for the reason byDelivered is: a
   *  page left open across a deployment. */
  satisfaction?: Satisfaction
  /** Visits counted by the value of each field an organisation has declared
   *  for the report surface, active or retired. See internal/report/fields.go
   *  and ruling 14 of the P10b plan (2026-09-25-p10b-screen-apps.md). Optional
   *  for the reason byDelivered is. */
  fieldCounts?: FieldCount[]
}

/** One row of a field's byValue table (internal/report/fields.go). */
export interface FieldValueCount {
  value: string
  visits: number
  /** The "not given" row. Its value still reads "not given", but a choice an
   *  organisation spelled that way is a real answer, and only this says which. */
  none?: boolean
}

/** A branch report's visits, counted by one declared field.
 *
 *  byValue is set only for a field that is not personal and is a choice or a
 *  boolean; every other field, personal ones included, is counted only as
 *  given and notGiven, whatever byValue would otherwise have shown — ruling
 *  14 draws that line, not the type alone. Given plus notGiven always equals
 *  the report's own visit count. */
export interface FieldCount {
  key: string
  name: Record<string, string>
  /** The declaration's type; a boolean's "true" and "false" are worded for the
   *  reader rather than printed as stored. Absent from an older server. */
  type?: string
  personal: boolean
  byValue?: FieldValueCount[]
  given: number
  notGiven: number
}

/** One line of the Customer feedback table. `served` is visits for a desk or a
 *  person and pieces of work for a service; `meanScore` is over the `scored`
 *  presses only. A row whose desk, person or service is gone is labelled
 *  "(removed)", a press with nobody signed in has key "" and the label
 *  "(nobody signed in)", one with no service "(no service)": English for the CSV,
 *  said in the reader's language on the screen (lib/satisfaction). */
export interface SatisfactionRow {
  key: string
  label: string
  presses: number
  served: number
  byCode: Record<string, number>
  scored: number
  meanScore: number
}

/** The lists may come as null: the network report sends one nobody filled. */
export interface Satisfaction {
  codes: string[]
  /** Each button's words by code, then language; absent for a code no longer in
   *  the catalogue, and from a server older than this field. */
  names?: Record<string, Record<string, string>>
  total: SatisfactionRow
  byDesk: SatisfactionRow[]
  byPerson: SatisfactionRow[]
  byService: SatisfactionRow[]
}

/** One line of the delivered table. `timed` is how many of `count` had something
 *  to be timed from; the times are over those. See internal/report/delivered.go. */
export interface DeliveredRow {
  serviceId: string
  serviceCode: string
  deliveredServiceId: string
  deliveredCode: string
  outcomeCode?: string
  count: number
  timed: number
  totalSec: number
  meanSec: number
}

/** One desk's or one person's row in the time tables, over the period. See
 *  internal/report/presence.go for what each field means. */
export interface TimeRow {
  key: string
  label: string
  sessions: number
  signedInSec: number
  /** The sum of the five fields below: pending, lunch, short break, task and
   *  break (earlier), kept as its own field because the old total still has
   *  to add up the same way it always did. */
  pausedSec: number
  /** Waiting on a decision — any kind, including one later declined or
   *  cancelled, since the wait happened regardless of the answer. */
  pendingSec: number
  lunchSec: number
  shortBreakSec: number
  taskSec: number
  /** Paused time no desk_request row explains: a branch paused before this
   *  figure existed, or an engine path that pauses without one. Shown on the
   *  screen as "Break (earlier)". */
  breakEarlierSec: number
  availableSec: number
  servingSec: number
  wrapUpSec: number
  /** Shown on the screen as "Ready" — available minus serving minus wrap-up. */
  idleSec: number
  served: number
  utilisation: number
  servedPerHour: number
}

/** One desk_request in the period, for the Breaks table: who asked, at which
 *  desk, for what, how long it was asked for and how long the break actually
 *  ran, and who (if anybody) decided it. See BreakRow in
 *  internal/report/presence.go. Unclipped, unlike the time tables above —
 *  this is a record of what was asked, not a sum of seconds. */
export interface BreakRow {
  staffName: string
  counterCode: string
  kind: string
  /** A short break's set length, on rows from before 31 Oct 2026 only. */
  minutes?: number
  requestedAt: string
  state: string
  decidedByName?: string
  startedAt?: string
  endedAt?: string
  /** The break reason's name as chosen; empty before reasons. */
  reason?: string
  /** How long the break itself ran, started to ended (or to now, for one
   *  still running); zero for a request that never started one. */
  seconds: number
}

/** One branch's row in the network comparison.
 *
 *  A deliberately narrow selection of the branch summary, and the server chose it:
 *  everything here is a figure a head office compares *between* branches, and the
 *  breakdowns by service, desk and operator are not — one branch's counter 3 means
 *  nothing beside another's.
 *
 *  The shares arrive alongside their counts and are used rather than recomputed.
 *  A network figure built by averaging branch percentages weights a branch that saw
 *  nine customers the same as one that saw nine hundred, and the server has already
 *  added the counts and divided once. */
export interface NetworkBranchLine {
  branchId: string
  branchCode: string
  branchName: string

  issued: number
  served: number
  noShow: number
  voided: number
  stillOpen: number

  medianWaitSec: number
  medianServeSec: number

  /** The service level as counts: how many waits were decided, and how many of
   *  those were inside the promise. */
  measured: number
  within: number
  /** The no-show denominator. Nobody can fail to answer a call they never
   *  received, so the rate is against customers who were called. */
  called: number

  /** The same two as fractions, sent so that every client draws the same
   *  percentages instead of each rounding its own. */
  withinTarget: number
  noShowRate: number
}

/** Every branch the signed-in person may see, side by side.
 *
 *  Sorted worst service level first by the server: somebody who opens this to find
 *  the branch that needs help should not have to sort it.
 *
 *  `totals` carries no medians, and that absence is deliberate rather than a gap —
 *  a median cannot be added up, and the mean of twelve medians is a number no
 *  branch would recognise. Whatever renders this must not print them as zero. */
export interface NetworkSummary {
  range: { from: string; to: string }
  branches: NetworkBranchLine[]
  totals: NetworkBranchLine
}

/** Where the installation stands with its licence.
 *
 *  Only the installation's owner ever sees this: what was bought, and for how much
 *  of it, is between them and us. */
export type LicenceState =
  | 'unlicensed'
  | 'valid'
  | 'not_yet_valid'
  | 'expired'
  | 'foreign'
  | 'needs_reactivation'
  | 'needs_newer_server'

export interface LicenceDocument {
  licenceId: string
  customer: { id: string; name: string }
  deployment: 'onprem' | 'saas'
  model: 'perpetual' | 'subscription'
  notBefore: string
  notAfter?: string
  maintenanceUntil?: string
  limits: {
    staff: number
    branches: number
    tenants: number
    devicesSoft: number
  }
  modules?: Record<string, number>
  features?: string[]
  binding: { mode: string; installId?: string; hostMatch?: number }
  issuedAt: string
}

export interface LicenceStatus {
  state: LicenceState
  licence?: LicenceDocument
  installId: string
  maintenanceExpired: boolean
  branches: number
  devices: number
  hostMatch: number
  mismatches: number
  appliedAt?: string
  appliedBy?: string
  overSoftLimits?: string[]
  locksConfiguration: boolean
  /** Every module this build has something delivered for, each marked bought or
   *  not — so the screen can answer "where is Analytics?", which a list of only
   *  what is on cannot. A module this build knows about but has not built does not
   *  appear here at all; see pendingModules. */
  modules: { name: string; label: string; kind: 'branch' | 'platform'; bought: boolean; seats?: number; every?: boolean; used?: number }[]
  /** Modules this licence grants that this build has never heard of: a file issued
   *  for a later version. Shown, not refused. */
  unknownModules?: string[]
  /** Modules this licence grants that this build knows about but has not built
   *  yet — a module sold ahead of its release. Also shown, not refused, and told
   *  apart from unknownModules by a sentence that can name the module. */
  pendingModules?: string[]
  /** Each counted limit with what is used against it. A limit without its usage is
   *  a number nobody can act on. */
  counted: { name: string; used: number; allowed: number; soft?: boolean }[] | null
  /** An edge showing the licence its server sent. */
  fromServer?: boolean
  /** An organisation in the cloud reading its plan as its licence. */
  plan?: boolean
  /** What counted counts: the whole installation, or the caller's organisation. */
  scope?: 'installation' | 'organisation'
}

/** A module sold per branch, as the Branches screen sees it. */
export interface BranchModule {
  name: string
  label: string
  bought: boolean
  seats: number
  every: boolean
  used: number
  branchIds: string[]
  /** Every reachable branch carrying a row for the module, retired ones
   *  included. A retired branch holds no seat but keeps its row, and while it
   *  does it cannot come back into service when every seat is taken. Optional
   *  so an older server's answer still reads. */
  heldIds?: string[]
}

/**
 * One installation setting, as the server describes it.
 *
 * The server sends what the value is *and* what the configuration file says, even
 * when nothing is overriding it. A person about to put a setting back needs to see
 * where it will land before they let go of it, not after.
 */
export interface SettingView {
  name: string
  kind: 'duration' | 'choice' | 'count' | 'days'
  choices?: string[]
  value: string
  fileValue: string
  setHere: boolean
  updatedAt?: string
  updatedBy?: string
}

/** One answer to "what was done for this customer". A service with any of these
 *  available requires one at close; a service with none behaves as it always did. */
export interface Outcome {
  id: string
  tenantId?: string
  serviceId?: string
  code: string
  name: Record<string, string>
  sortOrder: number
  active: boolean
}

/** A specific thing done under a service: the customer asked for "Accounts" and
 *  received "Close joint account". Always under one service. A service with any
 *  active one requires an entry before its piece of work ends. */
export interface DeliveredService {
  id: string
  tenantId?: string
  serviceId: string
  code: string
  name: Record<string, string>
  sortOrder: number
  active: boolean
  outcomes?: DeliveredOutcome[]
}

/** A result of one delivered service. Required when its delivered service has any
 *  active one. */
export interface DeliveredOutcome {
  id: string
  deliveredServiceId: string
  code: string
  name: Record<string, string>
  sortOrder: number
  active: boolean
}

/** Something else worth counting about a piece of work — "interpreter needed",
 *  "first visit", "complaint". The same shape as an Outcome and a different idea:
 *  several land on one transaction, and none of them is ever required. A branch
 *  that has defined none is shown no control at all. */
export interface Mark {
  id: string
  tenantId?: string
  serviceId?: string
  code: string
  name: Record<string, string>
  sortOrder: number
  active: boolean
  /** A button on a desk's feedback tablet: pressed by the customer, never offered
   *  to the clerk, and recorded apart from the clerk's marks. Fixed when the mark
   *  is made, so its history stays in one place. See catalog.Mark. */
  customer?: boolean
  /** What a customer's button is worth in an average, 1 to 5; absent or 0 for a
   *  button that is a count and not a point on a scale. */
  score?: number
}

export type FieldType = 'text' | 'number' | 'date' | 'boolean' | 'choice' | 'phone'

/** A place a field's value may be shown. See internal/catalog/field.go: "ticket" and
 *  "kiosk" designs both count as this — the same one customer either way — a hall
 *  board reads as "display", "counter" is declared but read by nothing yet, and
 *  "report" is read by the branch report's "By visit field" section (2026-09-25,
 *  Part D of the P10b plan). Named `FieldSurface` because `Surface` is a name worth
 *  keeping free for something the rest of the app might need it for. */
export type FieldSurface = 'ticket' | 'counter' | 'display' | 'report'

/** A visit field: what an organisation says a ticket may carry beyond its number,
 *  and which surfaces may show the value. Named `VisitField` rather than `Field`
 *  because `Field` is already the form-field component every admin screen is built
 *  from — the two would shadow each other in the file that needs both. */
export interface VisitField {
  id: string
  tenantId?: string
  /** What a layout writes and a bundle sends. Set once, at creation: the server
   *  ignores a key sent on an update, because a rename would silently blank every
   *  layout that referenced it. */
  key: string
  name: Record<string, string>
  type: FieldType
  /** Whether this is personal data. Does not by itself restrict where the value is
   *  shown — surfaces does that — it is how the installation answers what personal
   *  data it holds. */
  personal: boolean
  /** Where the value may appear. Empty means stored and never shown. */
  surfaces: FieldSurface[]
  /** Bounds a text value. Zero means the server's own default. */
  maxLen?: number
  /** The permitted values of a choice field; ignored by every other type. */
  choices?: string[]
  /** Days after issue that a value is kept. 0 keeps it as long as the ticket. */
  retentionDays?: number
  sortOrder: number
  active: boolean
}

export type TextTrigger = 'issued' | 'ahead' | 'called'

/** An organisation's texts to its customers: whether they are sent, from which
 *  field, at which moments, in which words, to which numbers. See
 *  internal/notify/settings.go's Settings. */
export interface NotificationSettings {
  enabled: boolean
  phoneField: string
  issued: boolean
  aheadAt: number
  called: boolean
  templates: Partial<Record<TextTrigger, Record<string, string>>>
  allowedPrefixes: string[]
}

/** Where an organisation's texts go, and how the request is built. The
 *  installation owner's alone — see internal/sms/gateway.go's Gateway. */
export interface SMSGateway {
  method: 'GET' | 'POST'
  url: string
  headers?: Record<string, string>
  contentType?: string
  body?: string
  successMatch?: string
  stripPlus?: boolean
  /** Ask again after a timeout or 5xx once the request was sent. */
  retryUnconfirmed?: boolean
}

export interface NotificationsView {
  settings: NotificationSettings
  /** The gateway whole, only for whoever may change it (tenant.manage); the
   *  organisation's administrator gets gatewayHost and gatewayConfigured. */
  gateway?: SMSGateway
  gatewayHost: string
  gatewayConfigured: boolean
  secretSet: boolean
  samples: { trigger: TextTrigger; language: string; text: string; segments: number }[]
}

/** A queue's alert levels, as saved (seconds). See internal/store/queuealert.go's
 *  QueueAlert. */
export interface QueueAlertSetting {
  queueId: string
  waitingAlert: number
  waitingReset: number
  longestAlertSec: number
  longestResetSec: number
  textTo: string[]
}

/** A queue over its level now, as the live channel carries it. */
export interface QueueAlert {
  queueId: string
  since: string
  reason: 'waiting' | 'longest'
}

/**
 * One line of the audit trail.
 *
 * The labels are stored alongside the identifiers on purpose, so a row still reads
 * after the person, branch or organisation it names has been renamed or removed —
 * which is exactly when an old row is being read.
 */
export interface AuditEvent {
  id: string
  tenantId?: string
  branchId?: string
  actorId?: string
  actorLabel: string
  actorRole?: string
  action: string
  outcome: 'allowed' | 'denied' | 'failed'
  targetKind?: string
  targetId?: string
  targetLabel?: string
  /** What is specific to this action: the permission that was missing, the role
   *  before and after, how many tickets a day-end closed. Never a secret. */
  detail?: Record<string, unknown>
  remoteAddr?: string
  userAgent?: string
  at: string
  /** The access token the actor used, when they acted through one. */
  viaToken?: ViaToken | null
}

/** An endpoint the organisation's events are sent to (internal/webhook.Endpoint).
 *  The secret is never here: it is shown once, from the create or new-secret answer. */
export interface Webhook {
  id: string
  tenantId: string
  name: string
  url: string
  events: string[]
  branchIds: string[]
  active: boolean
  activeSince: string
  createdAt: string
  updatedAt: string
}

/** A connector: an address an organisation's screen apps may call by name
 *  (internal/connector.Connector). The secret is never sent; hasSecret is all a
 *  screen is told. */
export interface Connector {
  id: string
  tenantId: string
  name: string
  url: string
  /** In the canonical order GET POST PUT PATCH DELETE; the first is what a call
   *  naming no method uses. */
  methods: string[]
  /** The paths below url an app may call: exact, or a prefix ending in /*. */
  paths: string[]
  headerName: string
  hasSecret: boolean
  timeoutMs: number
  active: boolean
  createdAt: string
  updatedAt: string
}

/** One row of a connector's call log: never a body, a path or a query. */
export interface ConnectorCall {
  id: number
  connectorId: string
  /** Empty when the machine is not known; the codes are empty once it is gone. */
  unitId: string
  branchCode: string
  unitCode: string
  at: string
  method: string
  /** 0 when no answer came. */
  status: number
  durationMs: number
  outcome: string
}

export interface WebhookTestResult {
  delivered: boolean
  status: number
  tookMs: number
  error?: string
}

export type DeliveryChannel = 'webhook' | 'sms' | 'email'
export type DeliveryStatus = 'pending' | 'delivered' | 'failed'

/** One message the outbox tried to send (internal/api deliveryView). */
export interface Delivery {
  id: string
  channel: DeliveryChannel
  target: string
  endpointGone?: boolean
  branchId?: string
  subject?: string
  status: DeliveryStatus
  attempts: number
  nextAttemptAt?: string
  lastError?: string
  createdAt: string
  deliveredAt?: string
  personal: boolean
  canResend: boolean
  /** 'code': a confirmation code, never sent again (the customer asks anew). */
  resendNote?: 'code' | 'outdated' | 'changed' | 'superseded' | 'noService'
}

// Reports by email (batch 4 part J): internal/api/reportschedules.go.
export type ReportFrequency = 'daily' | 'weekly' | 'monthly'

/** What became of a schedule's last letter, read back from the outbox (org.Delivery). */
export interface ReportDelivery {
  status: 'pending' | 'delivered' | 'failed'
  attempts: number
  lastError?: string
  deliveredAt?: string
}

/** A branch's report, or the network's (no branchId), on a timer (scheduleView). */
/** What a letter sends (migration 0080): the summary, a saved Figures view, or a Tickets view. */
export type ScheduleKind = 'summary' | 'view' | 'tickets'
export type ScheduleFormat = 'csv' | 'xlsx' | 'pdf'

export interface ReportSchedule {
  id: string
  tenantId: string
  branchId?: string
  branchName?: string
  /** The branches the letter covers; none is every branch. */
  branchIds?: string[]
  branchNames?: string[]
  kind: ScheduleKind
  viewId?: string
  viewName?: string
  /** With problem view_unavailable: the person who saved the view has left. */
  viewOwnerLeft?: boolean
  format: ScheduleFormat
  frequency: ReportFrequency
  sendMinute: number
  recipients: string[]
  enabled: boolean
  lastPeriodFrom?: string
  problem?: string
  mailProblem?: '' | 'none' | 'off' | 'unreadable'
  last?: ReportDelivery
  createdAt: string
  updatedAt: string
}

export interface ScheduleList {
  schedules: ReportSchedule[]
  allowedDomains: string[]
  canNetwork: boolean
  /** A Tickets letter may cover every active branch. */
  canAllBranches: boolean
}

export interface ScheduleDraft {
  branchId?: string
  /** Sent instead of branchId: none for every branch, several for a saved report or Tickets letter. */
  branchIds?: string[]
  kind: ScheduleKind
  viewId?: string
  format: ScheduleFormat
  frequency: ReportFrequency
  sendMinute: number
  recipients: string[]
  enabled: boolean
}

/** An organisation's mail server as the owner edits it (org.MailServer). */
export interface MailServerDraft {
  host: string
  port: number
  security: 'starttls' | 'tls' | 'none'
  username: string
  fromAddress: string
  caPem: string
  allowedDomains: string[]
  enabled: boolean
}

/** The mail server as the server answers it: never the password, only whether one is stored. */
export interface MailServerSettings extends MailServerDraft {
  passwordSet: boolean
  passwordUnreadable?: boolean
  updatedAt?: string
}

/** The per-ticket report (internal/report/tickets.go). */
export type TicketResult = 'served' | 'redirected' | 'no_show' | 'cancelled' | 'left' | 'not_reached'

/** One piece of work on one ticket. Times are RFC 3339 instants: show them on
 *  the branch's clock (timezone), never the browser's. */
export interface TicketReportRow {
  ticketId: string
  step: number
  branch: string
  employee?: string
  section: string
  desk?: string
  number: string
  date: string
  /** The branch's IANA zone. */
  timezone: string
  takenAt: string
  startedAt?: string
  endedAt?: string
  serviceSec?: number
  waitSec?: number
  result: TicketResult
  redirect?: 'service' | 'back' | 'person' | 'desk'
  /** The service a transfer went to, or the desk a send went to. */
  redirectWhere?: string
  redirectedBy?: string
  redirectedTo?: string
  online: boolean
  /** The access tokens behind the step's actions, by name. */
  viaToken?: string
}

export interface TicketReportPage {
  rows: TicketReportRow[]
  /** Present only when another ticket follows. */
  next?: string
}

export interface TicketReportSummary {
  tickets: number
  steps: number
  services: { id: string; name: string }[]
  staff: { id: string; name: string }[]
}

/** A screen's cached files: how many, their bytes, and its space. */
export interface ScreenCache {
  files: number
  bytes: number
  limit: number
  at: string
}

/** A playlist item a hall TV skipped; count is its skips reported since the server started. */
export interface MediaSkip {
  assetId: string
  name?: string
  kind: 'image' | 'video'
  at: string
  count: number
}

/** An organisation's data feed for its own BI (Administration → Data feed). */
export interface Feed {
  login: string
  database: string
  enabled: boolean
  /** A script waits for the bank's DBA: this server's database user may not make the login. */
  awaitingDba: boolean
  keySet: boolean
  keyCreatedAt?: string
  updatedAt?: string
  host: string
  port: number
  /** host reaches only the server itself: reporting.public_host is not set. */
  hostLocal?: boolean
  /** A login of this name exists that is not this database's: the server never touches it. */
  loginNotOurs?: boolean
  /** That login may still sign in, whatever the feed here says. */
  loginCanSignIn?: boolean
  /** The server makes and changes the login itself; otherwise the DBA runs the script. */
  serverMakesLogins?: boolean
  /** The DBA's script while it waits (a SCRAM verifier, never the password), or their choices for a login not ours. */
  script?: string
}

/** What Check found; problems are the server's codes, empty when all is well. */
export interface FeedCheck {
  roleExists: boolean
  canLogin: boolean
  grants: boolean
  connects: boolean
  turnedOn: boolean
  problems: string[]
}

/** Every feed answer: the password or key only in the one answer that made it. */
export interface FeedAnswer {
  feed: Feed
  /** Turn off left the login alone: it is not this database's, and may still sign in. */
  loginUntouched?: boolean
  password?: string
  script?: string
  key?: string
  check?: FeedCheck
}

// ---- Profiles (F0 contract, docs/PROFILES-API.md) ----

export interface ProfileProblem { path: string; code: string; message: string; args?: Record<string, unknown>; warning?: boolean }

// Operation profile document
export type QueueType = 'normal' | 'appointment'
export type SegmentAppointment = '' | 'yes' | 'no'
export type ProfilePool = 'desk' | 'branch' | 'staff'
export interface ProfileQueue {
  id: string; code: string; name: Record<string, string>; letter: string
  firstNumber: number; lastNumber: number; serviceLevelSec: number; type: QueueType; sortOrder: number
}
export interface ProfileSegment {
  id: string; name: string; serviceIds?: string[]; priorities?: string[]; languages?: string[]
  sources?: string[]; appointment?: SegmentAppointment; lateOverMin?: number; queueId: string
}
export interface ServiceGroup { id: string; name: string; serviceIds: string[]; defaultQueueId: string; segments: ProfileSegment[] }
export type CallOrder = 'single' | 'defined' | 'max_waiting' | 'max_lifetime' | 'service_level' | 'do_not_call' | 'priority_then_waiting' | 'fair_share' | 'closest_to_target'
export type PrimaryMode = 'ignore' | 'with' | 'without' | 'both'
export type RuleConditionKind = 'wait_over' | 'over_service_level' | 'time_between' | 'visits_count' | 'called_count' | 'caller_is_primary' | 'no_primary' | 'appointment_late'
export interface RuleCondition {
  kind: RuleConditionKind; queueId?: string; op?: 'lt' | 'gt' | 'eq'; n: number
  scope?: 'branch' | 'profile' | 'user'; from: number; until: number
}
export interface CallRule {
  id: string; queueIds: string[]; pools?: ProfilePool[]; order: CallOrder
  shares?: Record<string, number>; conditions?: RuleCondition[]; primaryResource: PrimaryMode
}
export interface ProfileWorkProfile {
  id: string; name: string; queueIds: string[]; pools?: ProfilePool[]; rules: CallRule[]
  wrapUpSec: number; noShowSec: number; recallIntervalSec: number; repeatCallSec: number; appointmentEarlySec?: number
}
export interface OperationDoc { groups: ServiceGroup[]; queues: ProfileQueue[]; workProfiles: ProfileWorkProfile[] }

// Equipment profile document
export type ProfileUnitKind = 'service_point' | 'kiosk' | 'display' | 'desk_display' | 'feedback'
export type ServicePointVerb = 'transfer' | 'send' | 'park' | 'recall' | 'walk_direct' | 'quick_serve'
/** Absent = inherit; present = this level's value, taken whole. withheld: [] = nothing withheld; layoutId/ticketLayoutId: '' = the default. */
export interface UnitSettings {
  withheld?: ServicePointVerb[]; printer?: PrinterSettings; layoutId?: string; ticketLayoutId?: string
  voice?: VoiceSettings; callPage?: CallPageSettings
}
export interface ProfileUnit { id: string; kind: ProfileUnitKind; name: string; defaultCount: number; settings: UnitSettings }
export interface EquipmentDoc { units: ProfileUnit[] }

// Branch document
export interface BranchSettings {
  rolloverMinute: number; autoEndOfDayMin: number; noAutoEndOfDay?: boolean; openingHours: DayHours[]
}
export interface UnitInstance {
  number: number; code: string; name: string; desk?: string; deskFilter?: string[]
  overrides: UnitSettings; sortOrder: number
}
export interface UnitInstances { profileUnitId: string; instances: UnitInstance[] }
export interface BranchDoc { operationProfileId: string; equipmentProfileId: string; settings: BranchSettings; units: UnitInstances[] }
export interface ProfileSnapshot<D> { profileId: string; revision: number; document: D }
export interface PublishedSetup { branch: BranchDoc; operation: ProfileSnapshot<OperationDoc>; equipment: ProfileSnapshot<EquipmentDoc> }
export type StatusReason = 'branch' | 'operation' | 'equipment' | 'never'

// Rows and answers
export interface OperationProfileRow { id: string; name: string; description: string; revision: number; branchCount: number; pendingCount: number; savedAt: string }
export interface EquipmentProfileRow { id: string; operationProfileId: string; name: string; description: string; revision: number; branchCount: number; pendingCount: number; savedAt: string }
/** A branch whose draft or published snapshot points at the profile (R5). */
export interface ProfileBranch {
  id: string; code: string; name: string
  revision: number                                   // branch draft revision
  operationProfileId: string; operationRevision: number   // draft pointer, that profile's current revision
  equipmentProfileId: string; equipmentRevision: number
  status: { reasons: StatusReason[] }
}
export interface OperationDocumentAnswer {
  profile: OperationProfileRow; revision: number; document: OperationDoc
  branches: ProfileBranch[]; warnings: ProfileProblem[]
}
export interface EquipmentDocumentAnswer {
  equipmentProfile: EquipmentProfileRow; revision: number; document: EquipmentDoc
  branches: ProfileBranch[]; kindDefaults: Record<ProfileUnitKind, UnitSettings>
}
export interface BranchSetup {
  branchId: string; draft: BranchDoc; published: PublishedSetup | null; version: number; revision: number
  publishedAt?: string; publishedBy?: string; publishedByName?: string
  draftSavedAt: string; draftSavedBy?: string; draftSavedById?: string
  operationRevision: number   // current revision of the draft's operation profile, 0 when none
  equipmentRevision: number
}
/** One machine of a branch's floor in a change list. */
export interface FloorMachine { id: string; code: string; kind: ProfileUnitKind; name: string; layoutId?: string; ticketLayoutId?: string }
/** What a publish does (or did) to a branch's machines; moved ones kept code and id under another profile unit. */
export interface FloorChanges {
  created: FloorMachine[]; dropped: FloorMachine[]
  moved: Array<FloorMachine & { changed: string[] }>; kept: Array<FloorMachine & { changed: string[] }>
  designs: Array<{ id: string; code: string; kind: ProfileUnitKind; name: string; field: 'layoutId' | 'ticketLayoutId'; from: string; to: string }>
}
/** One thing behind a status reason: a pointer or revision, a setting, or a machine by code. */
export interface ReasonDetail {
  reason: StatusReason; path: string; machine?: string; change: 'added' | 'removed' | 'changed'
  fields?: string[]; from?: unknown; to?: unknown
}
export interface BranchSetupAnswer {
  config: BranchSetup; changed: boolean
  status: { reasons: StatusReason[]; details: ReasonDetail[]; machines: FloorChanges | null }
  warnings: ProfileProblem[]
}
/** A save is live: version is what the floor runs after it; published is false when nothing differed. */
export interface SaveBranchSetupAnswer {
  revision: number; overwrote: string; overwroteAt?: string; draft: BranchDoc; warnings: ProfileProblem[]
  version?: number; published?: boolean; changes?: FloorChanges
}
/** Branches a shared profile's save could not put live (a rare race); the save itself stands. */
export type NotLive = Array<{ id: string; code: string; name: string }>
export interface SetupVersion {
  version: number; note: string; publishedAt: string; publishedBy: string; publishedByName?: string
  operation: { profileId: string; name: string; revision: number }
  equipment: { profileId: string; name: string; revision: number }
}
export interface SetupStatusLine {
  branchId: string; branchCode: string; branchName: string; active: boolean
  version: number; revision: number; changed: boolean; reasons: StatusReason[]
  operationProfileId: string; operationProfileName: string; operationRevision: number
  equipmentProfileId: string; equipmentProfileName: string; equipmentRevision: number
  published: { operationProfileId: string; operationRevision: number; equipmentProfileId: string; equipmentRevision: number } | null
  publishedAt?: string; publishedBy?: string; publishedByName?: string
  draftSavedAt?: string; draftSavedBy?: string; draftSavedById?: string; groupIds: string[]
}
export interface CounterSessionView { id: string; counterId: string; staffId: string; workProfileId: string; state: CounterState }

// Profile file and import
export interface ProfileFile {
  format: 'qms.operation-profile'; version: 1; exportedAt: string; name: string; description: string
  /** serviceIds (groups and segments) hold service CODES. */
  document: OperationDoc
  /** layoutId and ticketLayoutId are stripped: designs travel by their own export. */
  equipmentProfiles: Array<{ name: string; description: string; document: EquipmentDoc }>
}
export interface ImportRequest { file: ProfileFile; onto?: string; name?: string; baseRevision?: number; organisation?: string }
export interface ImportItem { kind: 'group' | 'segment' | 'queue' | 'workProfile' | 'equipmentProfile' | 'unit'; name: string; parent?: string }
export interface ProfileImportPreview {
  mode: 'new' | 'onto'; profileId?: string; profileName: string
  added: ImportItem[]; changed: ImportItem[]; kept: ImportItem[]; notInFile: ImportItem[]
  unknownServices: string[]; problems: ProfileProblem[]
}

// ---- end Profiles ----

/** A name and mobile the organisation has seen on a visit or a booking. */
export interface KnownCustomer {
  name?: string
  phone?: string
  lastSeen: string
}

/** One organisation's word for a platform phrase in one language (Settings › Wording). */
export interface WordingOverride {
  key: string
  lang: string
  text: string
  updatedAt: string
}

/** A server phrase (SMS, e-mail, slip) with its shipped text per language. */
export interface ServerPhrase {
  key: string
  text: Record<string, string>
}

/** GET /organisation/wording. */
export interface WordingView {
  organisation: string
  overrides: WordingOverride[]
  server: ServerPhrase[]
  maxLength: number
}
